import React, { useContext, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'

import { adminGetRole, adminGetRolesWithQuery } from '../../../domain/domain'

import { IPermission, IRoleResponse } from '../../../domain/interfaces/IRole'
import { formatStandardRoleName } from '../../components/datagrid/gridData/roles'
import { areArraysEqual, getEnabledNotificationKeys } from './helpers'
import { useOrganizationProvider } from '../../../providers/OrganizationProvider'

export interface RolePageContextType {
  loading: boolean
  error: string | undefined
  role: IRoleResponse | undefined
  id: string | undefined
  roleData: IPermission[]
  setRoleData: React.Dispatch<React.SetStateAction<IPermission[]>>
  formData: IPermission[]
  setFormData: React.Dispatch<React.SetStateAction<IPermission[]>>
  getSelectRole: (selectedRoleId: string) => void
  systemRoleOptions: { name: string; id: string }[]
  activeSystemRole: { name: string; id: string } | undefined
  setActiveSystemRole: React.Dispatch<React.SetStateAction<{ name: string; id: string } | undefined>>
  roleLoading: boolean
  newRoleOptions: { value: string; name: string }[]
  isSystemRole: boolean
  hasChanged: (newName: string) => boolean
  hideForms: boolean
  checkFormNotificationsSet: () => boolean
}

const RolePageContext = React.createContext<RolePageContextType | null>(null)
RolePageContext.displayName = 'RolePageContext'

interface RolePageVMProps {
  children: React.ReactNode
}

export const RolePageViewModel: React.FC<RolePageVMProps> = ({ children }) => {
  const [loading, setLoading] = useState<boolean>(false)
  const [roleLoading, setRoleLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | undefined>(undefined)
  const [role, setRole] = useState<IRoleResponse | undefined>(undefined)
  const { roles } = useOrganizationProvider()

  const [roleData, setRoleData] = useState<IPermission[]>([])
  const [formData, setFormData] = useState<IPermission[]>([])
  const [hideForms, setHideForms] = useState<boolean>(false)
  const [newRoleOptions, setNewRoleOptions] = React.useState<{ value: string; name: string }[]>([])
  const [isSystemRole, setIsSystemRole] = React.useState<boolean>(true)

  const { id } = useParams()
  const systemRoleOptions = roles
    ? roles
        .filter(role => role.isSystemRole && role.name.toLocaleLowerCase() !== 'super_admin')
        .map(role => {
          return { name: formatStandardRoleName(role.name), id: role.id }
        })
    : []

  const [activeSystemRole, setActiveSystemRole] = useState<{ name: string; id: string } | undefined>(
    systemRoleOptions?.find((role: any) => role.name === 'User'),
  )

  const getRole = async (selectedRoleId?: string) => {
    let userRoleId
    if (selectedRoleId) {
      userRoleId = selectedRoleId
    } else {
      userRoleId = systemRoleOptions?.find((role: any) => role.name === 'User')?.id
    }
    const roleId = id ? id : userRoleId
    if (!roleId) return
    setLoading(true)
    setError(undefined)
    const res = await adminGetRole(roleId ?? '')

    if (res.right) {
      setRole(res.right)
      setRoleData(res.right.features)
      // add feature access flag to forms for consistency with UI
      const formsWithFeatureAccessFlag = res.right.forms.map(addFeatureAccessToForm)
      setFormData(formsWithFeatureAccessFlag)
      if (!res.right.isSystemRole) {
        setIsSystemRole(false)
        await getRoles()
      }
      setLoading(false)
      return
    }
    setLoading(false)
    setError(res.left?.message ?? 'Error loading roles.')
  }
  const getRoles = async () => {
    const resRoles = await adminGetRolesWithQuery('&skip=0&limit=100')
    if (resRoles.right) {
      setNewRoleOptions(
        resRoles.right.data
          .map(role => {
            return { value: role.id, name: role.name }
          })
          .filter(roleOption => roleOption.value !== id),
      )
    }
  }
  const getIdAndPermissionAndFeature = (permission: IPermission) => {
    return {
      id: permission.id,
      permissions: permission.permissions,
      featureAccess: permission?.featureAccess ?? 0,
    }
  }

  const getFormData = (permission: IPermission) => {
    return {
      id: permission.id,
      permissions: permission.permissions,
      subscriptionData: permission.subscriptionData ?? {},
      notificationTypes: permission.notificationTypes,
    }
  }

  const hasChanged = (newName: string) => {
    const originalName = role?.name ?? ''
    if (newName !== originalName) return true
    const originalFeatures = role?.features.map(getIdAndPermissionAndFeature) ?? []
    const currentRoles = roleData.map(getIdAndPermissionAndFeature)
    const originalForms = role?.forms.map(getFormData) ?? []
    const currentForms = formData.map(getFormData)
    const isSame = areArraysEqual(currentRoles, originalFeatures) && areArraysEqual(originalForms, currentForms)
    return !isSame
  }

  const checkFormNotificationsSet = () => {
    return formData.some(fd => fd.formNotificationEnabled && fd.notificationTypes && fd.notificationTypes.length == 0)
  }

  const getSelectRole = async (selectedRoleId: string) => {
    setRoleLoading(true)
    setError(undefined)
    const res = await adminGetRole(selectedRoleId)
    setRoleLoading(false)
    if (res.right) {
      setRole(res.right)
      setRoleData(res.right.features)
      const formsWithFeatureAccessFlag = res.right.forms.map(addFeatureAccessToForm)
      setFormData(formsWithFeatureAccessFlag)
      return
    }
    setError(res.left?.message ?? 'Error loading roles.')
  }
  const addFeatureAccessToForm = (item: IPermission) => {
    const notificationTypes = getEnabledNotificationKeys(item.subscriptionData!)
    const allZero = item.permissions && Object.values(item.permissions).every(value => value === 0)
    return {
      ...item,
      featureAccess: allZero ? 0 : 1,
      isForm: true,
      formNotificationEnabled: notificationTypes.length > 0,
      subscriptionType: item.subscriptionData?.subscriptionType ?? ('Daily' as const),
      notificationTypes: notificationTypes ?? [],
    }
  }

  useEffect(() => {
    getRole()
  }, [])
  useEffect(() => {
    const subFeature = roleData.find(data => data.name === 'Submission Management')
    if (!subFeature) return
    if (hideForms && subFeature.featureAccess === 1) {
      setHideForms(false)
      return
    }
    if (!hideForms && subFeature.featureAccess === 0) {
      setHideForms(true)
      return
    }
  }, [roleData])

  return (
    <RolePageContext.Provider
      value={{
        role,
        error,
        loading,
        id,
        roleData,
        setRoleData,
        formData,
        setFormData,
        getSelectRole,
        systemRoleOptions,
        activeSystemRole,
        setActiveSystemRole,
        roleLoading,
        newRoleOptions,
        isSystemRole,
        hasChanged,
        hideForms,
        checkFormNotificationsSet,
      }}
    >
      {children}
    </RolePageContext.Provider>
  )
}

export function useRolePageViewModel(): RolePageContextType {
  const context = useContext(RolePageContext)
  if (!context) {
    throw new Error('useRolePageViewModel must be used within a RolePageViewModel')
  }
  return context
}
